import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { zim } from '../../../zegoConfig';
import { loginToZego } from '../../../components/utils/zegoLogin';
import axios from '../../../api/axios';
import { sendMessageToReactNativeApp } from "../../../components/utils/sendMessageToReactNativeApp";
import { ZIM as ZimType, ZIMMessage } from 'zego-zim-web';
import { decryptFromBackend } from '../../../utils/urlDecryptor';
import { useAuthStore } from '../../../stores/authStores';
import HeaderWithBack from '../../../components/HeaderWithBack';
import DoctorSwitchModal, { DoctorOption } from '../../../components/modal/DoctorSwitchModal';

interface TeleconsultationData {
  patient_id: string;
  patient_name: string;
  practitioner_id: string;
  practitioner_role_id: string;
  teleconsultation_start_time: string | null;
  doctor_assign_status: 'assigned' | 'unassigned';
}

interface UserData {
  id: string;
  person: {
    name: string;
  };
  practitioner_role_id: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Stage = sesi yang sedang berjalan di halaman ini, dibaca dari query param
//   ?stage=initial       -> SESI 1: request awal menunggu dokter ACCEPT (maks 3 menit)
//   ?stage=final-confirm -> SESI 2: sudah bayar, menunggu dokter MULAI konsultasi (maks 3 menit)
// ─────────────────────────────────────────────────────────────────────────────
type Stage = 'initial' | 'final-confirm';

// Phase = tampilan apa yang aktif di dalam SESI 1
type Phase =
  | 'searching'
  | 'waiting_accept'
  | 'accepted'
  | 'waiting_confirm'
  | 'started'
  | 'ended';

const ConnectingDoctor: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { teleconsultationId, token } = useParams<{
    teleconsultationId: string;
    token: string;
  }>();

  const stage = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get('stage') as Stage) || 'initial';
  }, [location.search]);

  const effectiveToken = useMemo(() => {
    const raw = token && token !== 'undefined' ? token : null;
    if (raw) return raw;
    const stored = teleconsultationId && teleconsultationId !== 'undefined'
      ? localStorage.getItem(`teleconsult_token_${teleconsultationId}`)
      : null;
    return stored || '';
  }, [token, teleconsultationId]);

  const decryptedToken = useMemo(() => {
      const t = effectiveToken;
      if (!t) return '';
      try {
        return decryptFromBackend(t);
      } catch (err) {
        console.error('Failed to decrypt token:', err);
        return '';
      }
  }, [effectiveToken]);

  useEffect(() => {
    if (token && token !== 'undefined' && teleconsultationId && teleconsultationId !== 'undefined') {
      localStorage.setItem(`teleconsult_token_${teleconsultationId}`, token);
    }
  }, [token, teleconsultationId]);

  // ─── States umum ────────────────────────────────────────────────────────
  const [userData, setUserData] = useState<UserData | null>(null);
  const [teleconsultationData, setTeleconsultationData] = useState<TeleconsultationData | null>(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // ─── States SESI 1 (initial) ────────────────────────────────────────────
  const [phase, setPhase] = useState<Phase>(stage === 'final-confirm' ? 'waiting_confirm' : 'searching');
  const [fadeIn, setFadeIn] = useState(true);
  const [displayText, setDisplayText] = useState({ main: '', sub: '' });
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [isTimeout] = useState(false);

  // ─── States SESI 2 (final-confirm) ──────────────────────────────────────
  const [waitingDoctorStart, setWaitingDoctorStart] = useState(true);
  const [doctorStartWindow, setDoctorStartWindow] = useState<number | null>(null); // ⬅️ BARU: durasi window Sesi 2, sumber sendiri
  const [doctorStartTimeLeft, setDoctorStartTimeLeft] = useState<number | null>(null);
  // ─── States modal pilih dokter lain (dipakai SESI 2) ────────────────────
  const [showDoctorSwitchModal, setShowDoctorSwitchModal] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const autoSwitchShownRef = useRef(false);
  const declinedPromptedRef = useRef(false);
  const [switchingDoctorName, setSwitchingDoctorName] = useState('');

  const phaseTexts: Record<Phase, { main: string; sub: string }> = {
    searching: {
      main: 'Menghubungkan ke dokter Anda...',
      sub: 'Mohon tunggu, sistem sedang menghubungi dokter yang Anda pilih',
    },
    waiting_accept: {
      main: 'Menunggu dokter merespons...',
      sub: 'Dokter Anda sedang memproses permintaan konsultasi ini',
    },
    accepted: {
      main: 'Permintaan Diterima',
      sub: 'Mengalihkan ke halaman pembayaran...',
    },
    waiting_confirm: {
      main: 'Menunggu dokter memulai konsultasi...',
      sub: 'Pembayaran Anda berhasil. Dokter akan segera memulai sesi konsultasi.',
    },
    started: {
      main: 'Dokter siap!',
      sub: 'Mengalihkan ke ruang konsultasi...',
    },
    ended: {
      main: 'Konsultasi sudah tidak aktif',
      sub: 'Sesi ini sudah selesai atau dibatalkan.',
    },
  };

  // ─── Refs ──────────────────────────────────────────────────────────────────
  const previousUserDokterRef = useRef<string | null>(null);
  const isCancelledRef = useRef(false);

  const formatTime = useCallback((seconds: number): string => {
    const safeSeconds = Math.max(0, Math.floor(seconds));
    const mins = Math.floor(safeSeconds / 60).toString().padStart(2, '0');
    const secs = (safeSeconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  }, []);

  const handleNavigationError = useCallback(
    (error: any) => {
      if (isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 400)) {
        navigate('/not-found');
      } else if (error.status === 403) {
        navigate('/forbidden');
      } else {
        console.error('Unexpected error:', error);
      }
    },
    [navigate]
  );

  const fetchUserData = useCallback(async (): Promise<UserData | null> => {
    if (!decryptedToken) return null;
    try {
      const response = await axios.post('users', null, {
        headers: { Authorization: `Bearer ${decryptedToken}` },
      });
      const data = response.data.data;
      localStorage.setItem('userData', JSON.stringify(data));
      return data;
    } catch (error) {
      handleNavigationError(error);
      return null;
    }
  }, [decryptedToken, handleNavigationError]);

  const fetchTeleconsultationData = useCallback(async (): Promise<TeleconsultationData | null> => {
    if (!decryptedToken || !teleconsultationId) return null;
    try {
      const response = await axios.get(`teleconsult/${teleconsultationId}`, {
        headers: { Authorization: `Bearer ${decryptedToken}` },
      });
      const data = response.data;
      localStorage.setItem('teleconsultationData', JSON.stringify(data));
      return data;
    } catch (error) {
      handleNavigationError(error);
      return null;
    }
  }, [decryptedToken, teleconsultationId, handleNavigationError]);

  const isValidUserData = (data: any): data is UserData =>
    data && data.id && data.person?.name && data.practitioner_role_id;

  const isValidTeleconsultationData = (data: any): data is TeleconsultationData =>
    data &&
    String(data.id) === String(teleconsultationId) &&
    data.patient_id &&
    data.patient_name &&
    data.practitioner_id;

  // ─── ZIM ───────────────────────────────────────────────────────────────────
  const setupConversationListener = useCallback(
    (targetUserId: string) => {
      zim.off('messageReceived');
      zim.off('conversationChanged');
      zim.off('peerMessageReceived');

      zim.on(
        'peerMessageReceived',
        (_zim: any, { messageList }: { messageList: ZIMMessage[]; fromConversationID: string }) => {
          const accepted = messageList.filter(
            (msg: any) =>
              typeof msg.message === 'string' &&
              msg.message.startsWith('accepted') &&
              msg.message.includes(teleconsultationId || '')
          );
          if (accepted.length > 0 && teleconsultationId && effectiveToken) {
            navigate(`/chat/${teleconsultationId}/${effectiveToken}`);
          }
          }
          );

          zim.on('messageReceived', async (_zimInstance: any, messageList: any[]) => {
          const accepted = messageList.filter(
          (msg: any) =>
            typeof msg.message === 'string' &&
            msg.message.startsWith('accepted') &&
            msg.message.includes(teleconsultationId || '')
          );
          if (accepted.length > 0 && teleconsultationId && effectiveToken) {
            navigate(`/chat/${teleconsultationId}/${effectiveToken}`);
          }
          });

          zim.on('conversationChanged', async () => {
          const config = { nextMessage: 0, count: 30, reverse: true };
          try {
          const chatHistory = await zim.queryHistoryMessage(targetUserId, 0, config);
          const accepted = chatHistory.messageList.filter(
            (msg: any) => typeof msg.message === 'string' && msg.message.startsWith('accepted')
          );
          accepted.forEach((msg: any) => {
            const id = msg.message.split('|')[1]?.trim();
            if (id === teleconsultationId && effectiveToken) {
              navigate(`/chat/${teleconsultationId}/${effectiveToken}`);
            }
          });
          } catch (err) {
          console.error('Gagal ambil riwayat pesan:', err);
          }
          });
          },
    [teleconsultationId, navigate, effectiveToken]
  );

  const loginAndSetupZIM = useCallback(async () => {
    if (!teleconsultationData || !process.env.REACT_APP_ZEGOCLOUD_APP_ENV) return;
    if (!isValidTeleconsultationData(teleconsultationData)) return;

    try {
      const envSuffix = process.env.REACT_APP_ZEGOCLOUD_APP_ENV || '';
      const userId = `LMS${teleconsultationData.patient_id}${envSuffix}`;
      const username = teleconsultationData.patient_name;
      const practitionerId = `LMS${teleconsultationData.practitioner_id}${envSuffix}`;

      const loginSuccess = await loginToZego(userId, username);
      if (loginSuccess) {
        setupConversationListener(practitionerId);
        previousUserDokterRef.current = practitionerId;
      }
    } catch (error) {
      console.error('Login ZIM gagal:', error);
    }
  }, [teleconsultationData, setupConversationListener]);

  // ─── Initial data load ─────────────────────────────────────────────────────
  const loadInitialData = useCallback(async () => {
    try {
      const savedUserData = localStorage.getItem('userData');
      const savedTeleconsultationData = localStorage.getItem('teleconsultationData');

      let userDataValid = false;
      let teleconsultationDataValid = false;

      if (savedUserData) {
        try {
          const parsed = JSON.parse(savedUserData);
          if (isValidUserData(parsed)) {
            setUserData(parsed);
            userDataValid = true;
          }
        } catch {}
      }

      if (savedTeleconsultationData) {
        try {
          const parsed = JSON.parse(savedTeleconsultationData);
          if (isValidTeleconsultationData(parsed)) {
            setTeleconsultationData(parsed);
            teleconsultationDataValid = true;
          }
        } catch {}
      }

      if (!userDataValid || !teleconsultationDataValid) {
        const promises = [];
        if (!userDataValid) promises.push(fetchUserData());
        promises.push(fetchTeleconsultationData());

        const results = await Promise.all(promises);
        let idx = 0;
        if (!userDataValid && results[idx]) {
          setUserData(results[idx] as UserData);
          idx++;
        }
        if (!teleconsultationDataValid && results[idx]) {
          setTeleconsultationData(results[idx] as TeleconsultationData);
        }
      }

      setIsDataLoaded(true);
    } catch {
      setIsDataLoaded(true);
    }
  }, [fetchUserData, fetchTeleconsultationData]);

  useEffect(() => {
    loadInitialData();
    return () => {
      isCancelledRef.current = true;
    };
  }, [loadInitialData]);

  // ZIM hanya menyimpan satu handler per event dan handler tetap hidup setelah
  // halaman ini ditinggalkan. Lepas handler milik halaman ini supaya tidak
  // ikut berjalan (query + navigate) di room chat.
  useEffect(() => {
    return () => {
      zim.off('peerMessageReceived');
      zim.off('conversationChanged');
    };
  }, []);

  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const phaseRef = useRef<Phase>(phase);
  const checkoutRedirectedRef = useRef(false);
  const cancelRequestedRef = useRef(false);
  const zimReadyRef = useRef(false);

  const updatePhaseWithFade = useCallback((newPhase: Phase) => {
    if (phaseRef.current === newPhase) return;
    phaseRef.current = newPhase;
    setFadeIn(false);
    setTimeout(() => {
      setDisplayText(phaseTexts[newPhase]);
      setPhase(newPhase);
      setFadeIn(true);
    }, 400);
  }, []);

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${decryptedToken}` } }),
    [decryptedToken]
  );

  const syncAcceptanceTimer = useCallback(async () => {
    if (!teleconsultationId) return;
    try {
      const res = await axios.get('waiting-acceptance-time', {
        params: { teleconsult_id: teleconsultationId },
        ...authHeaders,
      });
      setTimeLeft(Math.max(0, Math.floor(Number(res.data?.time) || 0)));
    } catch (err) {
      console.error('Gagal mengambil waiting acceptance time', err);
    }
  }, [teleconsultationId, authHeaders]);

  const cancelBySystem = useCallback(async () => {
    if (cancelRequestedRef.current || !teleconsultationId) return;
    cancelRequestedRef.current = true;

    try {
      const res = await axios.post(`teleconsult/${teleconsultationId}/cancel-by-system`, {}, authHeaders);
      if (res.data?.phase === 'ended') {
        navigate('/home', { state: { reason: 'cancel_by_system' } });
        return;
      }
      cancelRequestedRef.current = false;
    } catch (err) {
      cancelRequestedRef.current = false;
      if (isAxiosError(err) && err.response?.status === 409) {
        syncAcceptanceTimer();
        return;
      }
      navigate('/home', { state: { reason: 'cancel_by_system' } });
    }
  }, [teleconsultationId, authHeaders, navigate, syncAcceptanceTimer]);

  const goToCheckout = useCallback(() => {
    if (!teleconsultationId) return;

    const savedPertanyaan = (() => {
      const saved = localStorage.getItem('teleconsult_disclaimer');
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch {}
      }
      return location.state?.pertanyaan ?? { berat: '', tinggi: '', lamaSakit: '', minumObat: '', riwayatCovid: '' };
    })();

    navigate(`/checkout-teleconsultation/${teleconsultationId}`, {
      replace: true,
      state: {
        isAuto: false,
        pertanyaan: savedPertanyaan,
        teleconsultationId,
      },
    });
  }, [teleconsultationId, navigate, location.state]);

  useEffect(() => {
    if (stage !== 'initial') return;
    syncAcceptanceTimer();
  }, [stage, syncAcceptanceTimer]);

  useEffect(() => {
    if (stage !== 'initial' || timeLeft === null) return;
    if (phase === 'accepted' || phase === 'started') return;

    if (timeLeft <= 0) {
      cancelBySystem();
      return;
    }

    const timer = setTimeout(() => {
      setTimeLeft((prev) => (prev === null ? null : Math.max(0, prev - 1)));
    }, 1000);

    return () => clearTimeout(timer);
  }, [stage, timeLeft, phase, cancelBySystem]);

  const DOCTOR_START_WINDOW_SEC = 180;
  const doctorStartWindowRef = useRef(DOCTOR_START_WINDOW_SEC);
  const [doctorStartCycle, setDoctorStartCycle] = useState(0);

  const syncDoctorStartTimer = useCallback(async () => {
    if (!teleconsultationId) return;
    try {
      const res = await axios.get(`teleconsult/${teleconsultationId}/doctor-start-deadline`, authHeaders);
      const windowSec = Math.max(1, Math.floor(Number(res.data?.window) || DOCTOR_START_WINDOW_SEC));
      const seconds = Math.max(0, Math.floor(Number(res.data?.time) || 0));
      doctorStartWindowRef.current = windowSec;
      setDoctorStartWindow(windowSec);
      setDoctorStartCycle(Number(res.data?.cycle_number) || 0);
      setDoctorStartTimeLeft(seconds > 0 ? seconds : windowSec);
    } catch {
      setDoctorStartWindow(doctorStartWindowRef.current);
      setDoctorStartTimeLeft((prev) => prev ?? doctorStartWindowRef.current);
    }
  }, [teleconsultationId, authHeaders]);

  useEffect(() => {
    if (stage !== 'final-confirm') return;
    syncDoctorStartTimer();
  }, [stage, syncDoctorStartTimer]);

  useEffect(() => {
    if (stage !== 'final-confirm' || !waitingDoctorStart || doctorStartTimeLeft === null) return;

    const timer = setTimeout(() => {
      setDoctorStartTimeLeft((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          setDoctorStartCycle((c) => c + 1);
          return doctorStartWindowRef.current;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [stage, waitingDoctorStart, doctorStartTimeLeft]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (stage === 'initial') syncAcceptanceTimer();
      if (stage === 'final-confirm') syncDoctorStartTimer();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [stage, syncAcceptanceTimer, syncDoctorStartTimer]);

  useEffect(() => {
    if (!isDataLoaded || !teleconsultationId) return;

    let stopped = false;

    const handlePayload = (payload: any): boolean => {
      const serverPhase = payload?.phase as Phase | undefined;

      switch (serverPhase) {
        case 'started':
          updatePhaseWithFade('started');
          setWaitingDoctorStart(false);
          if (effectiveToken) {
            setTimeout(() => navigate(`/chat/${teleconsultationId}/${effectiveToken}`, { replace: true }), 1000);
          }
          return true;

        case 'ended':
          navigate('/home', { replace: true, state: { reason: 'consultation-ended' } });
          return true;

        case 'accepted':
          if (stage === 'initial' && !checkoutRedirectedRef.current) {
            checkoutRedirectedRef.current = true;
            updatePhaseWithFade('accepted');
            setTimeout(goToCheckout, 1200);
            return true;
          }
          return false;

        case 'waiting_confirm':
          if (stage !== 'final-confirm') {
            navigate(`/connect-doctor/${teleconsultationId}/${effectiveToken}?stage=final-confirm`, { replace: true });
            return true;
          }
          updatePhaseWithFade('waiting_confirm');
          setWaitingDoctorStart(true);
          if (payload?.doctor_declined && !declinedPromptedRef.current) {
            declinedPromptedRef.current = true;
            autoSwitchShownRef.current = true;
            setSwitchError('Dokter tidak dapat menerima konsultasi Anda. Pembayaran Anda tetap berlaku, silakan pilih dokter lain.');
            setShowDoctorSwitchModal(true);
          }
          return false;

        case 'waiting_accept':
          updatePhaseWithFade('waiting_accept');
          if (!zimReadyRef.current) {
            zimReadyRef.current = true;
            loginAndSetupZIM();
          }
          return false;

        case 'searching':
          updatePhaseWithFade('searching');
          return false;

        default:
          return false;
      }
    };

    const loop = async () => {
      if (stopped) return;

      let finished = false;
      try {
        const res = await axios.get(`teleconsult/${teleconsultationId}/assign-status`, authHeaders);
        finished = handlePayload(res.data);
      } catch (err) {
        console.error('Polling error:', err);
      }

      if (finished || stopped) return;
      pollingRef.current = setTimeout(loop, 5000);
    };

    setDisplayText(phaseTexts[stage === 'final-confirm' ? 'waiting_confirm' : 'searching']);
    loop();

    return () => {
      stopped = true;
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, [isDataLoaded, teleconsultationId, stage]);

  useEffect(() => {
    if (stage !== 'final-confirm' || !waitingDoctorStart || isSwitching) return;
    if (doctorStartCycle >= 1 && !autoSwitchShownRef.current) {
      autoSwitchShownRef.current = true;
      setShowDoctorSwitchModal(true);
    }
  }, [stage, waitingDoctorStart, isSwitching, doctorStartCycle]);

  const handleConfirmSwitch = async (doctor: DoctorOption) => {
    setShowDoctorSwitchModal(false);
    setSwitchError(null);
    setIsSwitching(true);
    setSwitchingDoctorName(doctor.doctorName);

    const showSwitchFailure = (message?: string | null) => {
      setSwitchError(message || 'Dokter tidak dapat dipilih saat ini. Silakan pilih dokter lain.');
      setShowDoctorSwitchModal(true);
    };

    try {
      const response = await axios.post(
        `teleconsult/${teleconsultationId}/reassign-practitioner-after-payment`,
        { doctor_code: doctor.doctorId },
        authHeaders
      );

      if (response.data?.code === 200) {
        setWaitingDoctorStart(true);
        autoSwitchShownRef.current = false;
        declinedPromptedRef.current = false;
        const fresh = await fetchTeleconsultationData();
        if (fresh) setTeleconsultationData(fresh);
        await syncDoctorStartTimer();
      } else {
        showSwitchFailure(response.data?.message);
      }
    } catch (error) {
      showSwitchFailure(isAxiosError(error) ? error.response?.data?.message : null);
    } finally {
      setIsSwitching(false);
      setSwitchingDoctorName('');
    }
  };

  const handleBack = () => {
    sendMessageToReactNativeApp({ action: 'goBack' });
    navigate('/home');
  };

  const handleRetry = () => {
    navigate('/home');
  };

  const showMainUI =
    stage === 'initial' &&
    !isTimeout &&
    (phase === 'searching' || phase === 'waiting_accept');

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white flex flex-col items-center justify-between">
      <div className="fixed top-0 left-0 right-0 z-[10000]">
        <HeaderWithBack title="Connect Doctor" url="/home" />
      </div>

      <div className="flex flex-col items-center justify-center flex-grow mt-4 px-4 text-center">
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">

          {/* ───────────────── SESI 1 — searching / waiting_accept ───────────────── */}
          {showMainUI && (
            <div className="flex flex-col items-center gap-4 px-6 w-full max-w-sm">
              <img
                src="/logo-linksehat.png"
                alt="Loading"
                className="w-20 h-20 animate-pulse"
              />

              {timeLeft !== null && (
                <p className="text-2xl font-semibold text-black">{formatTime(timeLeft)}</p>
              )}

              <p className={`font-bold text-xl transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                {displayText.main}
              </p>
              <p className={`text-sm text-gray-500 transition-opacity duration-500 delay-100 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                {displayText.sub}
              </p>
            </div>
          )}

          {/* ───────────────── SESI 1 — dokter accept, minta bayar ───────────────── */}
          {stage === 'initial' && phase === 'accepted' && (
            <div className="flex flex-col items-center gap-3 px-6 w-full max-w-sm text-center">
              <img src="/logo-linksehat.png" alt="Loading" className="w-20 h-20 animate-pulse" />
              <h2 className="font-bold text-lg text-gray-900">Permintaan Diterima</h2>
              <p className="text-sm text-gray-500">Mengalihkan ke halaman pembayaran...</p>
            </div>
          )}

          {/* ───────────────── SESI 2 — menunggu dokter mulai konsultasi ───────────────── */}
          {stage === 'final-confirm' && waitingDoctorStart && !isSwitching && (
            <div className="flex flex-col items-center gap-4 px-6 w-full max-w-sm">
              <img
                src="/logo-linksehat.png"
                alt="Loading"
                className="w-20 h-20 animate-pulse"
              />

              {doctorStartTimeLeft !== null && (
                <p className="text-2xl font-semibold text-black">{formatTime(doctorStartTimeLeft)}</p>
              )}

              <p className="font-bold text-xl">
                {doctorStartCycle >= 1 ? 'Dokter belum merespons...' : 'Menunggu dokter memulai konsultasi...'}
              </p>
              <p className="text-sm text-gray-500">
                {doctorStartCycle >= 1
                  ? 'Anda dapat tetap menunggu atau beralih ke dokter lain. Pembayaran Anda tetap berlaku.'
                  : 'Pembayaran Anda berhasil. Dokter akan segera memulai sesi konsultasi.'}
              </p>

              {!showDoctorSwitchModal && doctorStartCycle >= 1 && (
                <button
                  onClick={() => setShowDoctorSwitchModal(true)}
                  className="mt-1 text-sm text-teal-600 underline underline-offset-2"
                >
                  Pilih dokter lain
                </button>
              )}
            </div>
          )}

          {isSwitching && (
            <div className="flex flex-col items-center gap-4 px-6 w-full max-w-sm">
              <img
                src="/logo-linksehat.png"
                alt="Loading"
                className="w-20 h-20 animate-pulse"
              />
              <p className="font-bold text-xl">Beralih ke {switchingDoctorName}...</p>
              <p className="text-sm text-gray-500">
                Menunggu {switchingDoctorName} menerima konsultasi Anda...
              </p>
            </div>
          )}

          {/* ───────────────── SESI 1 — gagal terhubung (dipakai utk error umum) ───────────────── */}
          {isTimeout && (
            <div className="absolute inset-0 bg-black/40 flex items-end z-50">
              <div className="bg-white rounded-t-2xl w-full p-6 relative">
                <button
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-lg"
                  onClick={handleBack}
                >
                  ✕
                </button>
                <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-white text-4xl">✖</span>
                </div>
                <h2 className="mt-4 text-lg font-bold text-center text-black">Gagal Menghubungkan</h2>
                <p className="text-sm text-gray-500 text-center mt-1">
                  Saat ini kami gagal menghubungkan Anda ke dokter. Silakan coba lagi.
                </p>
                <div className="flex gap-4 mt-6">
                  <button onClick={handleBack} className="flex-1 py-2 rounded-full border border-teal-500 text-teal-500">
                    Kembali
                  </button>
                  <button onClick={handleRetry} className="flex-1 py-2 rounded-full bg-teal-500 text-white">
                    Coba Lagi
                  </button>
                </div>
              </div>
            </div>
          )}

          <DoctorSwitchModal
            isOpen={showDoctorSwitchModal}
            token={decryptedToken}
            excludeRoleId={teleconsultationData?.practitioner_role_id}
            title="Dokter lain tersedia"
            description="Dokter belum memulai konsultasi. Pilih dokter lain yang siap membantu Anda sekarang, atau tetap tunggu dokter saat ini."
            stayLabel="Tetap tunggu dokter saat ini"
            confirmMessage={(doctor) => (
              <>
                Anda akan beralih konsultasi ke{' '}
                <span className="font-semibold text-gray-700">{doctor.doctorName}</span>.
                <br />
                Pembayaran yang sudah Anda lakukan tetap berlaku, konsultasi akan dilanjutkan dengan dokter baru ini.
              </>
            )}
            errorMessage={switchError}
            onClose={() => {
              setShowDoctorSwitchModal(false);
              setSwitchError(null);
              syncDoctorStartTimer();
            }}
            onConfirm={handleConfirmSwitch}
          />

        </div>
      </div>
    </div>
  );
};

export default ConnectingDoctor;